import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import StatusAvatar from '../../statusAvatar';
import HierarchicalChart from '../../charts/HierarchicalChart';
import { useGetTrainerAddonExpiriesQuery, useGetTrainerSalesGraphQuery } from '../../../services/trainer';
import { UserDetailRoute } from '../../../routes/routepath';
import './styles.scss';

// Pull a field from an object trying several possible key names (API shape flexible).
const pick = (o: any, ...keys: string[]) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

interface ExpiryRow {
  key:        string;
  userId:     string;
  name:       string;
  mobile:     string;
  photo?:     string;
  expiryDate: string;
}

const fmtDate = (d: string) => (d ? dayjs(d).format('DD-MM-YYYY') : '—');

const daysLeftLabel = (d: string) => {
  if (!d) return '';
  const diff = dayjs(d).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (diff <= 0) return 'Expires today';
  if (diff === 1) return 'Expires tomorrow';
  return `Expires in ${diff}d`;
};

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const now = dayjs();

  const { data: expiriesResp, isFetching: expiriesLoading } = useGetTrainerAddonExpiriesQuery(undefined);
  const { data: salesResp,    isFetching: salesLoading }    = useGetTrainerSalesGraphQuery({ month: now.month() + 1, year: now.year() });

  const rawExpiries: any[] = pick(expiriesResp, 'data', 'expiries', 'clients') ?? (Array.isArray(expiriesResp) ? expiriesResp : []);
  const expiries: ExpiryRow[] = rawExpiries.map((e, i) => ({
    key:        pick(e, '_id', 'id', 'membershipId') ?? `exp-${i}`,
    userId:     pick(e, 'userId', 'user', 'clientId', '_id', 'id'),
    name:       pick(e, 'name', 'userName', 'clientName') ?? '—',
    mobile:     pick(e, 'mobileNumber', 'mobile', 'phoneNumber', 'phone') ?? '—',
    photo:      pick(e, 'photo', 'profileImage', 'image'),
    expiryDate: pick(e, 'expiryDate', 'addonExpiryDate', 'expiry') ?? '',
  }));

  const salesData: any = pick(salesResp, 'data') ?? salesResp ?? {};
  const rawDays: any[] = pick(salesData, 'graph', 'days', 'chart') ?? (Array.isArray(salesData) ? salesData : []);
  const chartData = rawDays
    .map((d) => ({
      id:      pick(d, 'day', 'date'),
      name:    String(pick(d, 'day') ?? dayjs(pick(d, 'date')).date()),
      revenue: Number(pick(d, 'revenue', 'sales', 'amount') ?? 0),
    }))
    .filter((d) => d.revenue > 0);
  const totalRevenue = Number(pick(salesData, 'totalRevenue', 'totalSales', 'total') ?? chartData.reduce((s, d) => s + d.revenue, 0));
  const monthLabel = dayjs(`${pick(salesData, 'year') ?? now.year()}-${pick(salesData, 'month') ?? now.month() + 1}-01`).format('MMMM YYYY');

  return (
    <div className="td-dashboard">
      {/* ── Upcoming Add-On Expiries ── */}
      <div className="td-card">
        <div className="td-card-head">
          <h3 className="td-card-title">Add-On Renewals Due</h3>
          <span className="td-card-sub">Next 7 days</span>
        </div>

        <div className="td-expiry-list">
          {expiriesLoading && <div className="td-empty">Loading…</div>}
          {!expiriesLoading && expiries.length === 0 && (
            <div className="td-empty">No add-on memberships expiring in the next 7 days.</div>
          )}
          {!expiriesLoading && expiries.map((e) => (
            <div
              key={e.key}
              className="td-expiry-row"
              onClick={() => e.userId && navigate(`${UserDetailRoute}/${e.userId}/addon-service`)}
            >
              <StatusAvatar src={e.photo} size={40} />
              <div className="td-expiry-info">
                <div className="td-expiry-name">{e.name}</div>
                <div className="td-expiry-mobile">{e.mobile}</div>
              </div>
              <div className="td-expiry-meta">
                <div className="td-expiry-date">{fmtDate(e.expiryDate)}</div>
                <div className="td-expiry-days">{daysLeftLabel(e.expiryDate)}</div>
              </div>
              <RightOutlined className="td-expiry-arrow" />
            </div>
          ))}
        </div>
      </div>

      {/* ── This Month's Sales (same chart the superadmin dashboard uses) ── */}
      {salesLoading ? (
        <div className="td-card"><div className="td-empty">Loading…</div></div>
      ) : (
        <HierarchicalChart
          title={`Sales — ${monthLabel}`}
          amount={totalRevenue.toLocaleString('en-IN')}
          data={chartData}
          type="trainers"
        />
      )}
    </div>
  );
};

export default TrainerDashboard;
